import React, { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Always-visible "Install App" button in the menu.
 * - Chrome/Edge: uses `beforeinstallprompt` when fired by the browser
 * - iOS Safari: shows Add-to-Home-Screen instructions
 * - Other browsers / before prompt fires: shows generic install instructions
 * Hidden only when the app is already running in standalone (installed) mode.
 */
const InstallAppButton = ({ className = '' }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [platform, setPlatform] = useState('other'); // 'ios' | 'android' | 'desktop' | 'other'
  const [showHint, setShowHint] = useState(false);

  useEffect(() => {
    const standalone = window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    setIsStandalone(standalone);

    const ua = window.navigator.userAgent || '';
    if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) {
      setPlatform('ios');
    } else if (/Android/.test(ua)) {
      setPlatform('android');
    } else {
      setPlatform('desktop');
    }

    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onAppInstalled = () => {
      setDeferredPrompt(null);
      setIsStandalone(true);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try { await deferredPrompt.userChoice; } catch (_) {}
      setDeferredPrompt(null);
      return;
    }
    // Browser hasn't offered prompt yet — show platform-specific instructions
    setShowHint(true);
  };

  if (isStandalone) return null;

  const instructions = {
    ios: [
      'Tap the Share icon at the bottom of Safari.',
      'Scroll down and tap "Add to Home Screen".',
      'Tap "Add" in the top-right corner.',
    ],
    android: [
      'Open the Chrome menu (\u22EE) in the top-right.',
      'Tap "Install app" or "Add to Home screen".',
      'Confirm by tapping "Install".',
    ],
    desktop: [
      'Look for the install icon (\u2295) in your address bar.',
      'Or open the Chrome menu (\u22EE) and choose "Install WAYO\u2026".',
      'Confirm by clicking "Install".',
    ],
    other: [
      'Open this site in Chrome, Edge, or Safari.',
      'Use the browser menu to install or add to home screen.',
    ],
  };

  const title = platform === 'ios'
    ? 'Install WAYO on your iPhone'
    : platform === 'android'
      ? 'Install WAYO on Android'
      : 'Install WAYO';

  return (
    <>
      <Button
        onClick={handleInstall}
        data-testid="install-app-button"
        className={`bg-[#252525] hover:bg-[#FFD12D] text-[#FFD12D] hover:text-[#252525] font-semibold h-11 transition-all duration-300 shadow-md w-full ${className}`}
      >
        <Download className="w-4 h-4 mr-2" />
        Install App
      </Button>

      {showHint && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[1200] flex items-end sm:items-center justify-center bg-black/50 p-4"
          onClick={() => setShowHint(false)}
          data-testid="install-hint-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-[#252525] rounded-2xl p-6 max-w-sm w-full shadow-xl"
          >
            <h3 className="text-lg font-bold text-[#252525] dark:text-white mb-2">{title}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Add WAYO to your home screen for fast, app-like access.
            </p>
            <ol className="text-sm text-gray-700 dark:text-gray-300 space-y-2 list-decimal pl-5">
              {instructions[platform].map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
            <Button
              onClick={() => setShowHint(false)}
              className="mt-5 w-full bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D] font-semibold h-11"
            >
              Got it
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallAppButton;
